import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Target, 
  CheckSquare, 
  Calendar,
  TrendingUp,
  Flame,
  ArrowRight,
  Sparkles,
  MessageSquare,
  Zap,
  RefreshCw,
  ChevronRight,
  ListTodo,
  BarChart3,
  AlertTriangle,
  Clock,
  Bell,
  Download,
  Upload,
  Database,
  Loader2,
  Share2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { analyticsApi, promptsApi, goalsApi, tasksApi, seedApi, nextActionsApi, weeklySummaryApi, alertsApi, dataApi } from '../lib/api';
import { getGreeting, calculateGoalProgress } from '../lib/utils';
import { Progress } from '../components/ui/progress';
import SocialShareButtons from '../components/ui/SocialShareButtons';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [todayPrompt, setTodayPrompt] = useState(null);
  const [recentGoals, setRecentGoals] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [nextActions, setNextActions] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [dueToday, setDueToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingSummary, setRefreshingSummary] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showDataDialog, setShowDataDialog] = useState(false);
  const fileInputRef = useRef(null);

  const loadDashboardData = useCallback(async () => {
    try {
      // Seed data first
      await seedApi.seed().catch(() => {});
      
      const [analyticsRes, promptRes, goalsRes, tasksRes, actionsRes, summaryRes, dueTodayRes, dailySummaryRes] = await Promise.all([
        analyticsApi.get().catch(() => ({ data: null })),
        promptsApi.getToday().catch(() => ({ data: null })),
        goalsApi.getAll().catch(() => ({ data: [] })),
        tasksApi.getAll().catch(() => ({ data: [] })),
        nextActionsApi.get().catch(() => ({ data: [] })),
        weeklySummaryApi.get().catch(() => ({ data: null })),
        alertsApi.getDueToday().catch(() => ({ data: null })),
        tasksApi.getDailySummary().catch(() => ({ data: { tasks: [] } })),
      ]);
      
      // Get IDs of daily tasks completed today
      const completedTodayIds = new Set(
        (dailySummaryRes.data?.tasks || [])
          .filter(t => t.completed_today)
          .map(t => t.task_id)
      );
      
      setAnalytics(analyticsRes.data);
      setTodayPrompt(promptRes.data);
      setRecentGoals(goalsRes.data?.slice(0, 3) || []);
      // Filter out: done tasks AND daily tasks completed today
      setRecentTasks(
        tasksRes.data?.filter(t => 
          t.status !== 'done' && !completedTodayIds.has(t.id)
        ).slice(0, 5) || []
      );
      setNextActions(actionsRes.data || []);
      setWeeklySummary(summaryRes.data);
      setDueToday(dueTodayRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await dataApi.exportMyData();
      const data = response.data;
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `strategic-architect-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Backup downloaded! Contains ${data.statistics.total_goals} goals, ${data.statistics.total_tasks} tasks`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleImportData = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate it's a Strategic Architect backup
      if (!data.export_version || !data.goals) {
        toast.error('Invalid backup file format');
        return;
      }
      
      const response = await dataApi.importMyData(data);
      const result = response.data;
      
      toast.success(`Imported ${result.imported_goals} goals and ${result.imported_tasks} tasks!`);
      
      // Reload dashboard data
      loadDashboardData();
      setShowDataDialog(false);
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import data. Please check the file format.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRefreshSummary = async () => {
    setRefreshingSummary(true);
    try {
      const response = await weeklySummaryApi.refresh();
      setWeeklySummary(response.data);
      toast.success('Weekly summary updated!');
    } catch (error) {
      toast.error('Failed to refresh summary');
    } finally {
      setRefreshingSummary(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'goal': return Target;
      case 'task': return CheckSquare;
      case 'prompt': return Calendar;
      case 'learning': return BookOpen;
      default: return Zap;
    }
  };

  const getActionLink = (action) => {
    switch (action.action_type) {
      case 'goal': return '/goals';
      case 'task': return '/tasks';
      case 'prompt': return '/prompts';
      case 'learning': return '/modules';
      default: return '/dashboard';
    }
  };

  const stats = [
    {
      label: 'Modules Completed',
      value: analytics?.total_modules_completed || 0,
      icon: BookOpen,
      color: 'text-[#26C6DA]',
      bgColor: 'bg-[#26C6DA]/10',
    },
    {
      label: 'Active Goals',
      value: analytics?.total_goals || 0,
      icon: Target,
      color: 'text-[#8B5CF6]',
      bgColor: 'bg-[#8B5CF6]/10',
    },
    {
      label: 'Tasks Completed',
      value: analytics?.tasks_completed || 0,
      icon: CheckSquare,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
    },
    {
      label: 'Day Streak',
      value: analytics?.streak_days || 0,
      icon: Flame,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            {getGreeting()}, {user?.name?.split(' ')[0] || 'Strategist'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Here's your strategic overview for today.
          </p>
        </div>
        <div className="flex gap-3">
          {/* Data Backup Dialog */}
          <Dialog open={showDataDialog} onOpenChange={setShowDataDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full px-6 gap-2 border-slate-200 dark:border-[#1F2937] bg-white dark:bg-[#111827] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937] hover:text-[#26C6DA]" data-testid="backup-btn">
                <Database className="w-4 h-4" />
                Backup
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                  <Database className="w-5 h-5 text-[#26C6DA]" />
                  Data Backup & Restore
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Export Section */}
                <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl space-y-3 border border-slate-200 dark:border-[#1F2937]">
                  <div className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-[#26C6DA]" />
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100">Export Your Data</h3>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Download all your goals, tasks, and activities as a JSON backup file.
                  </p>
                  <Button 
                    onClick={handleExportData}
                    disabled={exporting}
                    className="w-full rounded-full bg-[#26C6DA] hover:bg-[#0891B2] text-black"
                    data-testid="export-data-btn"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Backup
                      </>
                    )}
                  </Button>
                </div>

                {/* Import Section */}
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-3">
                  <div className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-amber-400" />
                    <h3 className="font-semibold text-amber-300">Restore Data</h3>
                  </div>
                  <p className="text-sm text-amber-200/70">
                    Import goals and tasks from a previous backup file. This will add to your existing data.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                    id="import-file"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    variant="outline"
                    className="w-full rounded-full border-amber-500/50 text-amber-300 hover:bg-amber-500/20 bg-transparent"
                    data-testid="import-data-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Backup File
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showSummary} onOpenChange={setShowSummary}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full px-6 gap-2 border-slate-200 dark:border-[#1F2937] bg-white dark:bg-[#111827] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937] hover:text-[#26C6DA]" data-testid="weekly-summary-btn">
                <BarChart3 className="w-4 h-4" />
                Weekly Summary
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                  <span>Weekly Summary</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleRefreshSummary}
                    disabled={refreshingSummary}
                    className="text-[#26C6DA] hover:text-[#26C6DA] hover:bg-[#26C6DA]/10"
                    data-testid="refresh-summary-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${refreshingSummary ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </DialogTitle>
              </DialogHeader>
              
              {weeklySummary ? (
                <div className="space-y-6 mt-4">
                  {/* AI Insights */}
                  <div className="bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-2xl p-6 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5" />
                      <span className="font-medium">AI Insights</span>
                    </div>
                    <p className="text-white/90 leading-relaxed whitespace-pre-wrap">
                      {weeklySummary.ai_insights}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl p-4 text-center border border-slate-200 dark:border-[#1F2937]">
                      <p className="text-2xl font-bold text-[#26C6DA] font-mono">{weeklySummary.prompts_completed}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Prompts Answered</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl p-4 text-center border border-slate-200 dark:border-[#1F2937]">
                      <p className="text-2xl font-bold text-[#26C6DA] font-mono">{weeklySummary.tasks_summary?.completed || 0}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Tasks Completed</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl p-4 text-center border border-slate-200 dark:border-[#1F2937]">
                      <p className="text-2xl font-bold text-[#26C6DA] font-mono">{weeklySummary.goals_progress?.active || 0}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Active Goals</p>
                    </div>
                  </div>

                  {/* Key Achievements */}
                  {weeklySummary.key_achievements?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-emerald-400" />
                        Key Achievements
                      </h4>
                      <ul className="space-y-2">
                        {weeklySummary.key_achievements.map((achievement, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
                            <span className="text-emerald-400 mt-1">✓</span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Goals Progress */}
                  {weeklySummary.goals_progress?.details?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                        <Target className="w-4 h-4 text-[#26C6DA]" />
                        Goals Progress
                      </h4>
                      <div className="space-y-3">
                        {weeklySummary.goals_progress.details.map((goal, i) => (
                          <div key={i} className="bg-slate-100 dark:bg-[#1A1A2E] rounded-lg p-3 border border-slate-200 dark:border-[#1F2937]">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-200">{goal.title}</span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">{goal.progress}%</span>
                            </div>
                            <Progress value={goal.progress} className="h-2 bg-[#1F2937]" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommended Actions */}
                  {weeklySummary.recommended_actions?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-400" />
                        Recommended Next Steps
                      </h4>
                      <ul className="space-y-2">
                        {weeklySummary.recommended_actions.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
                            <ChevronRight className="w-4 h-4 text-[#26C6DA] mt-0.5" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center text-slate-500 dark:text-slate-400">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p>No summary data available yet. Keep using the app!</p>
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          <Link to="/coach">
            <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2" data-testid="chat-coach-btn">
              <MessageSquare className="w-4 h-4" />
              AI Coach
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={index}
            className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6 hover:border-[#26C6DA]/30 transition-colors"
            data-testid={`stat-card-${index}`}
          >
            <div className={`w-10 h-10 rounded-xl ${stat.bgColor} flex items-center justify-center ${stat.color} mb-3`}>
              <stat.icon className="w-5 h-5" />
            </div>
            <p className="text-3xl font-bold text-[#26C6DA] font-mono">{stat.value}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Due Today Alert */}
      {dueToday && (dueToday.total_due_today > 0 || dueToday.total_overdue > 0) && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-5" data-testid="due-today-alert">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Items Requiring Attention
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {dueToday.total_due_today > 0 && `${dueToday.total_due_today} due today`}
                {dueToday.total_due_today > 0 && dueToday.total_overdue > 0 && ' • '}
                {dueToday.total_overdue > 0 && <span className="text-red-400">{dueToday.total_overdue} overdue</span>}
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            {/* Overdue Items First */}
            {dueToday.tasks_overdue?.slice(0, 2).map((task, index) => (
              <Link key={`task-overdue-${index}`} to="/tasks" className="block">
                <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl hover:bg-red-500/20 transition-colors">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{task.content || task.title}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-red-400">Task overdue</p>
                      {task.recurring && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {task.recurring}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            {dueToday.action_items_overdue?.slice(0, 2).map((item, index) => (
              <Link key={`action-overdue-${index}`} to="/goals" className="block">
                <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl hover:bg-red-500/20 transition-colors">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{item.title}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-red-400">Action item overdue • {item.goal_title}</p>
                      {item.recurring && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {item.recurring}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs font-medium text-red-400">{item.progress}%</span>
                </div>
              </Link>
            ))}
            
            {/* Due Today Items */}
            {dueToday.tasks_due_today?.slice(0, 2).map((task, index) => (
              <Link key={`task-today-${index}`} to="/tasks" className="block">
                <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-[#1A1A2E] border border-amber-500/30 rounded-xl hover:bg-amber-500/10 transition-colors">
                  <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{task.content || task.title}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-amber-400">Task due today</p>
                      {task.recurring && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {task.recurring}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            {dueToday.action_items_due_today?.slice(0, 2).map((item, index) => (
              <Link key={`action-today-${index}`} to="/goals" className="block">
                <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-[#1A1A2E] border border-amber-500/30 rounded-xl hover:bg-amber-500/10 transition-colors">
                  <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{item.title}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-amber-400">Action item due today • {item.goal_title}</p>
                      {item.recurring && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {item.recurring}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{item.progress}%</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Next Action Items */}
      {nextActions.length > 0 && (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="next-actions-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-[#26C6DA]" />
              <span className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>Next Action Items</span>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6] font-medium">
              AI Suggested
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {nextActions.slice(0, 6).map((action, index) => {
              const Icon = getActionIcon(action.action_type);
              return (
                <Link 
                  key={action.id} 
                  to={getActionLink(action)}
                  className="group"
                  data-testid={`next-action-${index}`}
                >
                  <div className={`p-4 rounded-xl border transition-all duration-200 hover:-translate-y-0.5 ${
                    action.priority === 'high' 
                      ? 'bg-red-500/10 border-red-500/30 hover:border-red-400/50' 
                      : 'bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] hover:border-[#26C6DA]/50'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        action.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-[#26C6DA]/10 text-[#26C6DA]'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-800 dark:text-slate-100 text-sm truncate group-hover:text-[#26C6DA] transition-colors">
                          {action.title}
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                          {action.description}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-[#26C6DA] transition-colors flex-shrink-0" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Prompt */}
        <div className="lg:col-span-2 bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-2xl p-8 text-white relative overflow-hidden" data-testid="daily-prompt-card">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-white/80" />
              <span className="text-white/80 text-sm font-medium">Today's Prompt</span>
            </div>
            {todayPrompt ? (
              <>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>
                  {todayPrompt.title}
                </h3>
                <p className="text-white/90 text-lg leading-relaxed mb-6">
                  {todayPrompt.prompt_text}
                </p>
                <Link to="/prompts">
                  <Button className="bg-white text-[#8B5CF6] hover:bg-white/90 rounded-full px-6 gap-2 font-medium" data-testid="respond-prompt-btn">
                    Respond Now
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </>
            ) : (
              <p className="text-white/80">No prompt available today. Check back tomorrow!</p>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="progress-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#26C6DA]" />
            <span className="font-medium text-slate-800 dark:text-slate-100">Overall Progress</span>
          </div>
          <div className="text-center py-6">
            <div className="relative inline-flex items-center justify-center w-32 h-32">
              <svg className="w-full h-full -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  fill="none"
                  stroke="#1F2937"
                  strokeWidth="12"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  fill="none"
                  stroke="url(#progressGradient)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={`${(analytics?.overall_progress || 0) * 3.52} 352`}
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8B5CF6" />
                    <stop offset="100%" stopColor="#26C6DA" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="absolute text-3xl font-bold text-[#26C6DA] font-mono">
                {Math.round(analytics?.overall_progress || 0)}%
              </span>
            </div>
            <p className="text-slate-500 dark:text-slate-400 mt-4">Keep up the great work!</p>
          </div>
        </div>
      </div>

      {/* Goals and Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Goals */}
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="goals-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#26C6DA]" />
              <span className="font-medium text-slate-800 dark:text-slate-100">Active Goals</span>
            </div>
            <Link to="/goals" className="text-sm text-[#26C6DA] hover:underline" data-testid="view-all-goals-link">
              View all
            </Link>
          </div>
          
          {recentGoals.length > 0 ? (
            <div className="space-y-3">
              {recentGoals.map((goal) => (
                <div key={goal.id} className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-slate-800 dark:text-slate-100">{goal.title}</h4>
                    <span className="text-xs px-2 py-1 rounded-full bg-[#26C6DA]/10 text-[#26C6DA]">
                      {goal.category}
                    </span>
                  </div>
                  <Progress 
                    value={calculateGoalProgress(goal.key_results)} 
                    className="h-2 bg-[#1F2937]"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    {calculateGoalProgress(goal.key_results)}% complete
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 mb-3">No goals set yet</p>
              <Link to="/goals">
                <Button variant="outline" className="rounded-full border-slate-200 dark:border-[#1F2937] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937]" data-testid="create-goal-btn">
                  Create Your First Goal
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Recent Tasks */}
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="tasks-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-[#8B5CF6]" />
              <span className="font-medium text-slate-800 dark:text-slate-100">Pending Tasks</span>
            </div>
            <Link to="/tasks" className="text-sm text-[#26C6DA] hover:underline" data-testid="view-all-tasks-link">
              View all
            </Link>
          </div>
          
          {recentTasks.length > 0 ? (
            <div className="space-y-2">
              {recentTasks.map((task) => (
                <div key={task.id} className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                  <div className={`w-2 h-2 rounded-full ${
                    task.priority === 'high' ? 'bg-red-500' :
                    task.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                  }`} />
                  <span className="flex-1 text-slate-800 dark:text-slate-100">{task.title}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{task.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 mb-3">No pending tasks</p>
              <Link to="/tasks">
                <Button variant="outline" className="rounded-full border-slate-200 dark:border-[#1F2937] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937]" data-testid="create-task-btn">
                  Add a Task
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* AI Coach CTA */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6" data-testid="ai-coach-cta">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-1" style={{ fontFamily: 'Outfit' }}>
            Need Strategic Guidance?
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            Chat with your AI Strategy Coach for personalized advice on goals, planning, and execution.
          </p>
        </div>
        <Link to="/coach">
          <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-8 gap-2" data-testid="start-coaching-btn">
            Start Coaching Session
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      {/* Share Section */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-[#1A1A2E] dark:to-[#16213E] rounded-2xl p-6 border border-slate-200 dark:border-[#1F2937]">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-[#26C6DA]/20 flex items-center justify-center">
              <Share2 className="w-6 h-6 text-[#26C6DA]" />
            </div>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-1" style={{ fontFamily: 'Outfit' }}>
              Enjoying Strategic Architect?
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Share it with your network and help others achieve their goals too!
            </p>
          </div>
          <SocialShareButtons variant="icons-only" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
